/**
 * useLiveTranscript
 *
 * WebSocket hook that subscribes to live transcription segments for a given
 * group key (e.g. a check-in ID). Accumulates segments in local state and
 * provides connection / streaming status.
 *
 * Pass `groupKey = null` to skip connecting entirely.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

// ── Types ────────────────────────────────────────────────────────────────────

export interface TranscriptSegment {
  text: string;
  start_time: number;
  end_time: number;
  speaker: number | null;
}

interface UseLiveTranscriptResult {
  segments: TranscriptSegment[];
  isStreaming: boolean;
  isConnected: boolean;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_DELAY_MS = 2000;

function buildWsUrl(groupKey: string): string {
  // Use a dedicated WS host env var if provided (e.g. ws://localhost:9000 for
  // local ASGI server). Fall back to deriving from the HTTP backend host.
  const explicitWsHost = import.meta.env.VITE_APP_WS_HOST as string | undefined;

  let wsHost: string;
  if (explicitWsHost) {
    wsHost = explicitWsHost;
  } else {
    const httpHost =
      import.meta.env.VITE_APP_DJANGO_BACKEND_HOST ?? 'http://localhost:8000';
    wsHost = httpHost
      .replace(/^https:\/\//, 'wss://')
      .replace(/^http:\/\//, 'ws://');
  }

  // Strip trailing slash before appending path
  const base = wsHost.replace(/\/+$/, '');
  return `${base}/ws/live-transcript/${groupKey}/`;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useLiveTranscript(
  groupKey: string | null,
): UseLiveTranscriptResult {
  const [segments, setSegments] = useState<TranscriptSegment[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track whether the hook is still mounted to avoid state updates after cleanup
  const mountedRef = useRef(true);

  const cleanup = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    if (!groupKey) {
      cleanup();
      setIsConnected(false);
      setIsStreaming(false);
      return undefined;
    }

    // Reset segments when the groupKey changes
    setSegments([]);
    reconnectAttemptsRef.current = 0;

    function connect() {
      if (!mountedRef.current || !groupKey) return;

      const url = buildWsUrl(groupKey);
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) return;
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event: MessageEvent) => {
        if (!mountedRef.current) return;
        try {
          const data = JSON.parse(event.data as string) as {
            type: string;
            payload?: TranscriptSegment;
          };
          if (data.type === 'transcription_segment_added' && data.payload) {
            const segment = data.payload;
            setSegments((prev) => [...prev, segment]);
            setIsStreaming(true);
          } else if (data.type === 'call_ended') {
            setIsStreaming(false);
          }
        } catch {
          // Ignore malformed messages
        }
      };

      ws.onclose = () => {
        if (!mountedRef.current) return;
        setIsConnected(false);

        // Auto-reconnect with retry limit
        if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttemptsRef.current += 1;
          reconnectTimerRef.current = setTimeout(
            connect,
            RECONNECT_DELAY_MS,
          );
        } else {
          // Only clear streaming state when we've exhausted reconnect attempts
          setIsStreaming(false);
        }
      };

      ws.onerror = () => {
        // onclose will fire after onerror; reconnect is handled there
      };
    }

    connect();

    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, [groupKey, cleanup]);

  return { segments, isStreaming, isConnected };
}
